/**
 * Alert Modal Component
 * Reusable modal for displaying alerts, errors, and success messages
 */

import React from 'react';

export type AlertType = 'success' | 'error' | 'info' | 'warning';

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  type?: AlertType;
}

export const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: '✓',
          iconColor: '#27ae60',
          borderColor: '#27ae60',
          bgColor: '#d5f4e6',
        };
      case 'error':
        return {
          icon: '✕',
          iconColor: '#e74c3c',
          borderColor: '#e74c3c',
          bgColor: '#fadbd8',
        };
      case 'warning':
        return {
          icon: '⚠',
          iconColor: '#f39c12',
          borderColor: '#f39c12',
          bgColor: '#fef5e7',
        };
      default:
        return {
          icon: 'ℹ',
          iconColor: '#3498db',
          borderColor: '#3498db',
          bgColor: '#ebf5fb',
        };
    }
  };

  const styles = getTypeStyles();

  const defaultTitles: Record<AlertType, string> = {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Information',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="alert-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          borderTop: `4px solid ${styles.borderColor}`,
        }}
      >
        <div className="alert-modal-header" style={{ backgroundColor: styles.bgColor }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.5rem', color: styles.iconColor }}>
              {styles.icon}
            </span>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>
              {title || defaultTitles[type]}
            </h3>
          </div>
          <button 
            className="modal-close" 
            onClick={onClose}
            style={{ fontSize: '1.5rem', lineHeight: '1' }}
          >
            ×
          </button>
        </div>
        <div className="alert-modal-body">
          <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{message}</p>
        </div>
        <div className="alert-modal-footer">
          <button onClick={onClose} className="btn btn-primary">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};
